import React, { useEffect } from 'react';
import { Button, Modal, Form, Input, notification, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { SensorCreateData } from '../../../../redux/reducers/clients/clients.types';
import { clearSensorStatus, createSensorRequest } from '../../../../redux/reducers/clients/clientsSlice';
import { RootState } from '../../../../redux/store';

const { Option } = Select;

interface AddSensorModalProps {
    visible: boolean;
    onClose: () => void;
    clientId: string;
}

const AddSensorModal: React.FC<AddSensorModalProps> = ({ visible, onClose, clientId }) => {
    const { t } = useTranslation(['clients', 'common']);
    const dispatch = useDispatch();
    const [form] = Form.useForm();

    const { loading, error, success } = useSelector((state: RootState) => state.clients.sensors);
    const { data: gateways } = useSelector((state: RootState) => state.clients.gateways);

    const handleClose = () => {
        form.resetFields();
        dispatch(clearSensorStatus());
        onClose();
    };

    const onFinish = (values: Omit<SensorCreateData, 'companyId'>) => {
        if (clientId) {
            dispatch(createSensorRequest({ ...values, companyId: clientId }));
        }
    };

    useEffect(() => {
        if (success && visible) {
            notification.success({ message: t('clients:sensor_add_success') });
            handleClose();
        }
        if (error) {
            notification.error({ message: t(error) });
            dispatch(clearSensorStatus());
        }
    }, [success, error, visible]);

    return (
        <Modal
            centered={true}
            title={t('clients:add_new_sensor')}
            visible={visible}
            onCancel={handleClose}
            footer={[
                <Button key="back" onClick={handleClose}>{t('cancel')}</Button>,
                <Button style={{ backgroundColor: '#02b9d9' }} key="submit" type="primary" loading={loading} onClick={() => form.submit()}>
                    {t('save')}
                </Button>,
            ]}
        >
            <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false}>
                <Form.Item name="name" label={t('clients:name')} rules={[{ required: true, message: t('clients:field_is_required') }]}>
                    <Input placeholder={t('clients:enter_name')} />
                </Form.Item>
                <Form.Item name="serialno" label={t('clients:serial_number')} rules={[{ required: true, message: t('clients:field_is_required') }]}>
                    <Input placeholder={t('clients:enter_serial_number')} />
                </Form.Item>
                <Form.Item name="gatewayId" label={t('clients:linked_gateway')} rules={[{ required: true, message: t('clients:field_is_required') }]}>
                    <Select placeholder={t('clients:select_gateway')}>
                        {(gateways || []).map(g => <Option key={g.id} value={g.id}>{`${g.gateway} (${g.gsm})`}</Option>)}
                    </Select>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default AddSensorModal;